import { Extension } from '@tiptap/core';

export interface TextIndentOptions {
  types: string[];
  indentUnit: string;
  defaultIndentLevel: number;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    textIndent: {
      /**
       * Increase indentation
       */
      indent: () => ReturnType;
      /**
       * Decrease indentation
       */
      outdent: () => ReturnType;
    };
  }
}

export const TextIndent = Extension.create<TextIndentOptions>({
  name: 'textIndent',

  addOptions() {
    return {
      types: ['paragraph', 'heading'],
      indentUnit: '2ch',
      defaultIndentLevel: 0
    };
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.types,
        attributes: {
          indent: {
            default: this.options.defaultIndentLevel,
            parseHTML: element => {
              const paddingInlineStart =
                element.style.paddingInlineStart || element.style.paddingLeft;
              if (!paddingInlineStart) return this.options.defaultIndentLevel;

              // Extract numeric values (e.g., "4ch" -> 4, "2ch" -> 2)
              const value = parseFloat(paddingInlineStart);
              const unitValue = parseFloat(this.options.indentUnit);

              // Calculate and return the level
              return Math.round(value / unitValue);
            },
            renderHTML: attributes => {
              if (!attributes.indent) {
                return {};
              }

              // Extract numeric value and unit type from indentUnit (e.g., "2ch" -> 2 and "ch")
              const unitValue = parseFloat(this.options.indentUnit);
              const unitType = this.options.indentUnit.replace(/[\d.]/g, '');

              return {
                style: `padding-inline-start: ${attributes.indent * unitValue}${unitType}`
              };
            }
          }
        }
      }
    ];
  },

  addCommands() {
    return {
      indent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          let handled = false;

          // Check if any node in selection is a list item
          let inList = false;
          state.doc.nodesBetween(from, to, node => {
            if (node.type.name === 'listItem') {
              inList = true;
              return false;
            }
          });

          // If in a list, don't handle - let list commands handle it
          if (inList) {
            return false;
          }

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || this.options.defaultIndentLevel;
              tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                indent: currentIndent + 1
              });
              handled = true;
            }
          });

          if (handled && dispatch) {
            dispatch(tr);
          }

          return handled;
        },
      outdent:
        () =>
        ({ tr, state, dispatch }) => {
          const { selection } = state;
          const { from, to } = selection;
          let handled = false;

          // Check if any node in selection is a list item
          let inList = false;
          state.doc.nodesBetween(from, to, node => {
            if (node.type.name === 'listItem') {
              inList = true;
              return false;
            }
          });

          // If in a list, don't handle - let list commands handle it
          if (inList) {
            return false;
          }

          state.doc.nodesBetween(from, to, (node, pos) => {
            if (this.options.types.includes(node.type.name)) {
              const currentIndent = node.attrs.indent || this.options.defaultIndentLevel;
              const prevIndent = Math.max(0, currentIndent - 1);

              if (prevIndent !== currentIndent) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  indent: prevIndent
                });
                handled = true;
              }
            }
          });

          if (handled && dispatch) {
            dispatch(tr);
          }

          return handled;
        }
    };
  },

  addKeyboardShortcuts() {
    return {
      // Tab is reserved for form navigation, use Alt+M for indentation
      'Alt-m': () => this.editor.commands.indent(),
      'Alt-Shift-m': () => this.editor.commands.outdent(),
      Backspace: () => {
        const { state } = this.editor;
        const { selection } = state;
        const { $from } = selection;

        // Only handle if cursor is at the start of a text block
        if (!selection.empty || $from.parentOffset !== 0) {
          return false;
        }

        const node = $from.parent;
        const indent = node.attrs.indent;

        // If the node has indentation, outdent instead of deleting
        if (indent && indent > 0 && this.options.types.includes(node.type.name)) {
          return this.editor.commands.outdent();
        }

        return false;
      }
    };
  }
});
